import { required, styled, ToucanManageRows } from "toucan";
import {
  addTag,
  deleteTag,
  renameTag,
  setTagEmoji,
  sortedTags,
} from "../model.ts";
import { forgetListTag } from "../routes.ts";
import { store } from "../state.ts";
import type { ManageRowActions, ManageRowItem } from "toucan";

const Page = styled("section")`
  padding: 1rem;
  padding-top: 1.125rem;
  min-height: 100%;
  /* The same warm canvas the list sits on. */
  background: var(--toucan-canvas);
`;

/** The tag a row names. Every row is drawn from a tag that is there. */
function tagById(id: string) {
  return required(
    store().tags.find((entry) => entry.id === id),
    "tag",
  );
}

/**
 * How many people have a tag — shown per row so deleting one (which untags
 * everyone) isn't a silent surprise.
 */
function tagUseCount(tagId: string) {
  return store().people.filter((person) => person.tagIds.includes(tagId))
    .length;
}

/** What a row's controls mean here. Rows are only offered a Delete once
 * nobody has the tag, so removing one untags no-one. */
const actions: ManageRowActions = {
  onRename: (id, name) => {
    renameTag(tagById(id), name);
  },
  onEmoji: (id, emoji) => {
    setTagEmoji(tagById(id), emoji);
  },
  onDelete: (id) => {
    deleteTag(store(), id);
    // Leaving here opens the list this screen was opened from, and that
    // cannot be filtered to a deleted tag.
    forgetListTag(id);
  },
  onCreate: (name, emoji) => {
    addTag(store(), name, emoji === "" ? undefined : emoji);
  },
};

/**
 * The Manage tags screen. It is the platform's manage screen; what is here is
 * what a tag means to it: how many people carry one, and what renaming,
 * changing the icon, deleting or creating does to the store.
 */
export function TagsPage() {
  const items = sortedTags(store().tags).map((tag) => {
    const item: ManageRowItem = {
      id: tag.id,
      name: tag.name,
      uses: tagUseCount(tag.id),
    };
    if (tag.emoji !== undefined) {
      item.emoji = tag.emoji;
    }
    return item;
  });
  return (
    <Page className="page-column">
      <ToucanManageRows
        thing="Tag"
        noun="person"
        nouns="people"
        items={items}
        actions={actions}
      />
    </Page>
  );
}
